import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import RichText from "@/components/RichText";
import { useAuth } from "@/context/AuthContext";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { HERO_ART } from "@/lib/heroes";
import { fireGamify } from "@/lib/gamify";
import { fetchGamify } from "@/lib/api";
import { CheckCircle2, Flame, Circle } from "lucide-react";

// Nitya Puja — the traditional Panchopachara (five-fold) home worship, following
// the standard Smarta nitya-vidhi. Mantras are the canonical, widely-attested forms.
const STEPS = [
  { deva: "आचमन", en: "Achamana (purification)",
    mantra: "ॐ केशवाय नमः · ॐ नारायणाय नमः · ॐ माधवाय नमः",
    translit: "Om Keshavaya Namah · Om Narayanaya Namah · Om Madhavaya Namah",
    note_en: "Sip water thrice from the right palm, once with each name, then wipe the lips saying 'Om Govindaya Namah'.",
    note_hi: "दाहिनी हथेली से तीन बार जल का आचमन करें — प्रत्येक नाम के साथ एक बार — फिर 'ॐ गोविन्दाय नमः' कहते हुए ओष्ठ स्पर्श करें।" },
  { deva: "संकल्प", en: "Sankalpa (resolve)",
    mantra: "ॐ अद्य … (देश-काल स्मरण) … अस्माकं सकुटुम्बानां क्षेमस्थैर्यार्थं इष्टदेवता-पूजनम् अहं करिष्ये।",
    translit: "Om adya … (place & time) … kshema-sthairyartham ishta-devata-pujanam aham karishye.",
    note_en: "Holding water in the right hand, state the day, place and purpose, resolving to perform the worship of your Ishta Devata.",
    note_hi: "दाहिने हाथ में जल लेकर तिथि, स्थान एवं प्रयोजन का स्मरण करते हुए अपने इष्टदेव की पूजा का संकल्प लें।" },
  { deva: "ध्यान एवं आवाहन", en: "Dhyana & Avahana (meditation & invocation)",
    mantra: "ॐ श्री (इष्टदेवता) मूर्तये नमः · आवाहयामि",
    translit: "Om Shri (Ishta-devata) murtaye namah · avahayami",
    note_en: "Meditate upon the form of your chosen deity and invoke their presence into the murti or image with folded hands.",
    note_hi: "अपने इष्टदेव के स्वरूप का ध्यान करें और हाथ जोड़कर मूर्ति/चित्र में उनका आवाहन करें।" },
  { deva: "गन्ध (चन्दन)", en: "Gandha — sandal paste",
    mantra: "ॐ श्री (देवता) नमः · गन्धं समर्पयामि",
    translit: "Om Shri (devata) namah · gandham samarpayami",
    note_en: "Offer chandana (sandal paste) to the deity.",
    note_hi: "देवता को चन्दन अर्पित करें।" },
  { deva: "पुष्प", en: "Pushpa — flowers",
    mantra: "ॐ श्री (देवता) नमः · पुष्पाणि समर्पयामि",
    translit: "Om Shri (devata) namah · pushpani samarpayami",
    note_en: "Offer flowers, adding tulasi for Vishnu or bilva for Shiva as appropriate.",
    note_hi: "पुष्प अर्पित करें — विष्णु हेतु तुलसी एवं शिव हेतु बिल्वपत्र सहित।" },
  { deva: "धूप", en: "Dhupa — incense",
    mantra: "ॐ श्री (देवता) नमः · धूपम् आघ्रापयामि",
    translit: "Om Shri (devata) namah · dhupam aghrapayami",
    note_en: "Show lit incense before the deity.",
    note_hi: "देवता के समक्ष धूप दिखाएँ।" },
  { deva: "दीप", en: "Deepa — lamp",
    mantra: "ॐ श्री (देवता) नमः · दीपं दर्शयामि",
    translit: "Om Shri (devata) namah · dipam darshayami",
    note_en: "Show the lit ghee lamp before the deity.",
    note_hi: "देवता के समक्ष घृत-दीप दिखाएँ।" },
  { deva: "नैवेद्य", en: "Naivedya — food offering",
    mantra: "ॐ श्री (देवता) नमः · नैवेद्यं निवेदयामि",
    translit: "Om Shri (devata) namah · naivedyam nivedayami",
    note_en: "Offer prepared food (fruit, milk or a simple bhoga), sprinkling water around it.",
    note_hi: "भोग (फल, दूध अथवा सरल नैवेद्य) अर्पित करें एवं चारों ओर जल छिड़कें।" },
  { deva: "आरती एवं प्रणाम", en: "Aarti & Pranama",
    mantra: "ॐ श्री (देवता) नमः · कर्पूर-नीराजनं समर्पयामि · नमस्कारान् समर्पयामि",
    translit: "Om Shri (devata) namah · karpura-nirajanam · namaskaran samarpayami",
    note_en: "Perform aarti with camphor, offer pushpanjali, then prostrate and pray for the welfare of all.",
    note_hi: "कर्पूर से आरती करें, पुष्पांजलि अर्पित करें, फिर साष्टांग प्रणाम कर सर्वकल्याण की प्रार्थना करें।" },
];

export default function DainikPooja() {
  const { pick } = useLang();
  const { page } = useSettings();
  const P = page("/dainik-pooja");
  const pv = (o, d) => (pick(o?.hi, o?.en) || d);
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [done, setDone] = useState(false);
  const today = new Date().toDateString();

  useEffect(() => {
    if (user) fetchGamify().then(setG).catch(() => {});
    if (localStorage.getItem("dharmartha_pooja_day") === today) setDone(true);
  }, [user, today]);

  const complete = async () => {
    const res = await fireGamify("daily_pooja");
    localStorage.setItem("dharmartha_pooja_day", today);
    setDone(true);
    if (res?.profile) setG(res.profile);
  };

  return (
    <div data-testid="dainik-pooja-page">
      <Seo path="/dainik-pooja" title="Dainik Pooja — Daily Worship Vidhi with Mantras"
        description="A step-by-step daily puja (Panchopachara) for home worship — achamana, sankalpa, dhyana, and the five offerings with their canonical mantras, transliteration and meaning." />
      <PageHero art={HERO_ART.devotion} overline={pv(P.subtitle, pick("साधना · दैनिक पूजा", "Practice · Daily Pooja"))}
        deva="दैनिक पूजा" en={pv(P.title, pick("दैनिक पूजा — नित्य विधि", "Dainik Pooja — Daily Worship"))}
        tagline={pv(P.explainer, pick("घर पर नित्य पूजन की सरल पंचोपचार विधि — आचमन, संकल्प, ध्यान एवं पाँच उपचार, प्रामाणिक मंत्रों सहित।", "A simple Panchopachara vidhi for daily home worship — achamana, sankalpa, dhyana and the five offerings, with canonical mantras."))} />
      {(P.body?.en || P.body?.hi) && (
        <section className="mx-auto max-w-3xl px-5 pt-10 md:px-8" data-testid="dainik-body">
          <RichText value={pick(P.body?.hi, P.body?.en)} className="max-w-none font-serif-latin text-lg leading-relaxed text-ink-2" />
        </section>
      )}

      {/* Streak / completion */}
      <section className="mx-auto max-w-3xl px-5 pt-10 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 border border-line bg-surface p-5">
          <div className="flex items-center gap-6">
            <div><p className="font-serif-latin text-3xl text-saffron">{g?.pooja_streak ?? 0}</p><p className="text-xs text-ink-3">{pick("दिन निरंतर", "day streak")}</p></div>
            <div><p className="font-serif-latin text-3xl text-ink">{g?.pooja_days ?? 0}</p><p className="text-xs text-ink-3">{pick("कुल दिन", "total days")}</p></div>
          </div>
          {user ? (
            <button onClick={complete} disabled={done} data-testid="pooja-complete-button"
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors ${done ? "border border-green-600 text-green-600" : "bg-[var(--saffron)] text-white hover:bg-[var(--saffron-dark)]"}`}>
              {done ? <><CheckCircle2 className="h-4 w-4" /> {pick("आज पूर्ण", "Completed today")}</> : <><Flame className="h-4 w-4" /> {pick("आज की पूजा पूर्ण करें", "Mark today's pooja complete")}</>}
            </button>
          ) : (
            <Link to="/login" className="text-sm text-saffron">{pick("संकल्प संजोने हेतु लॉगिन करें", "Sign in to track your streak")}</Link>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-12 md:px-8">
        <div className="mb-8 border border-line bg-surface p-5">
          <p className="overline-label text-gold">{pick("पूजा सामग्री", "Pooja Samagri")}</p>
          <p className="mt-2 text-sm text-ink-2">{pick(
            "जल-पात्र (आचमनी), चन्दन, अक्षत (चावल), पुष्प एवं तुलसी/बिल्वपत्र, धूप, घृत-दीप, नैवेद्य (फल/मिष्ठान्न), घंटी एवं कर्पूर।",
            "A water vessel (achamani), chandana (sandal paste), akshata (unbroken rice), flowers with tulasi/bilva, incense, a ghee lamp, naivedya (fruit/sweets), a bell and camphor.")}</p>
        </div>
        <ol className="space-y-4">
          {STEPS.map((s, i) => (
            <li key={i} data-testid={`pooja-step-${i}`} className="border border-line bg-surface p-5">
              <div className="flex items-baseline gap-3">
                <Circle className="mt-1.5 h-2 w-2 shrink-0 fill-gold text-gold" />
                <div className="min-w-0">
                  <p className="font-deva text-xl text-ink deva-lh">{s.deva} <span className="font-serif-latin text-base text-ink-3">· {s.en}</span></p>
                  <p className="mt-2 font-deva text-lg leading-relaxed text-saffron deva-lh">{s.mantra}</p>
                  <p className="mt-1 text-sm italic text-ink-3">{s.translit}</p>
                  <p className="mt-2 text-sm leading-relaxed text-ink-2">{pick(s.note_hi, s.note_en)}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
        <p className="mt-8 border-l-2 border-gold/50 pl-4 text-sm leading-relaxed text-ink-3">
          {pick("यह सामान्य स्मार्त नित्य-विधि पर आधारित सरल पंचोपचार पूजा है। कृपया अपने कुल/गुरु परंपरा एवं इष्टदेव अनुसार मंत्र एवं उपचार का पालन करें।",
            "This is a simple Panchopachara puja based on the standard Smarta nitya-vidhi. Please follow the mantras and upacharas of your own family/guru parampara and Ishta Devata.")}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/rituals" className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("विस्तृत पूजा विधियाँ", "Detailed Pooja Guides")}</Link>
          <Link to="/sandhya-vandana" className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("संध्या वंदना", "Sandhya Vandana")}</Link>
          <Link to="/sadhana" className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("मेरी साधना", "My Sadhana")}</Link>
        </div>
      </section>
    </div>
  );
}
